// Shared projection for a quizPage (funnel) document. Extracted so both the
// slug lookup and the /ai-team-rating settings-driven lookup return the same
// shape without duplicating the (large) block projection.
const QUIZ_PAGE_PROJECTION = `{
    _id, title, slug, metaDescription, templateKey,
    theme,
    readinessScoring{
      ratingMin, ratingMax, baseMin, baseMax,
      captainMatchPoints, captainMismatchPoints,
      transferSelectedPoints, transferRollPoints,
      clampMin, clampMax,
      targetMates, targetWork, targetMoney
    },
    readinessRace{
      behindMinGap, aheadMinClear,
      projectionGameweek, behindMultiplier, behindCap, levelDrift,
      titlePacePercent, userMinPercent, userMaxPercent,
      gapToPercentScale, minSeparationPercent, ghostFloorPercent,
      aheadGhostDropPercent,
      paceMarkerLabel, paceStatusLabel
    },
    blocks[] {
      _type, _key,
      _type == "heroBlock" => { heading, subheading, backgroundImage, callToActionText, subtextBelow, isFullScreenHeight, hashSlug },
      _type == "quizStepBlock" => { question, answerLayout, options, nextButtonText, isRequired, hashSlug },
      _type == "emailCaptureBlock" => { heading, subheading, placeholderText, buttonText, hasMarketingConsent, consentLabelText, privacyPolicyUrl, hashSlug },
      _type == "llmResponseBlock" => { heading, loadingText, errorText, promptTemplate, hashSlug },
      _type == "teamInputBlock" => { heading, subheading, uploadLabel, textInputLabel, uploadHelpText, uploadDropzoneText, textInputPlaceholder, submitButtonText, hashSlug },
      _type == "loadingBlock" => { rotatingMessages, messageCycleIntervalMs, promptTemplate, errorText, hashSlug },
      _type == "resultBlock" => { labelText, primaryButtonText, primaryButtonIcon, secondaryButtonText, subtextBelow, hashSlug },
      _type == "handoffBlock" => { heading, valueProps[]{ icon, title, description }, buttonText, buttonUrl, hashSlug },
      _type == "ctaBlock" => { heading, bodyText, buttonText, buttonUrl, variant, hashSlug },
      _type == "gytrImportStepBlock" => {
        header{ eyebrow, heading, subtext }, hashSlug,
        layoutVariant, importButtonText, buildManuallyText, searchPlaceholder, searchSelectButtonText, proofLine, footnote, ocrReadErrorText
      },
      _type == "gytrManualEntryStepBlock" => {
        header{ eyebrow, heading, subtext }, hashSlug,
        budgetLabel, continueButtonText, backText
      },
      _type == "gytrEmailGateBlock" => {
        header{ eyebrow, heading, subtext }, hashSlug,
        trustpilotTemplateId, trustpilotHeight, trustpilotWidth, asSeenOnLabel, asSeenOnMonochrome, asSeenOnLogoHeight, asSeenOnItems[]{ _key, logo, altText, url },
        emailPlaceholder, consentText, invalidEmailText, ctaText
      },
      _type == "gytrProcessingBlock" => {
        header{ eyebrow, heading, subtext }, hashSlug,
        loadingSteps[]{ _key, label, durationMs }
      },
      _type == "gytrRevealBlock" => {
        header{ eyebrow, heading, subtext }, hashSlug,
        gauge{ subtext, bands[]{ _key, maxValue, colour } },
        insightLines{ enabled, lowProjectedStarter, flaggedStarter, strongFallback },
        ctaText
      },
      _type == "gytrTransferBlock" => {
        header{ eyebrow, heading, subtext }, hashSlug,
        gauge{ subtext, bands[]{ _key, maxValue, colour } },
        nowLabel, withFfhLabel, transferSectionLabel, transferSectionLabelPlural,
        outLabel, inLabel, lockedLabel, upliftLabel,
        pendingUpliftLabel, pendingNote,
        trajectoryBands[]{ _key, maxPercentile, line },
        ctaText, loginLinkText, loginUrl
      },
      _type == "readinessTeamImportBlock" => {
        topBanner{ eyebrow, heading, subtext }, hashSlug,
        layoutVariant,
        screenshotSectionLabel, screenshotUploadLabel, screenshotHelpText, ocrReadErrorText,
        searchDividerText, searchSectionLabel, searchPlaceholder, searchSelectButtonText,
        buildTeamLinkText, ctaText
      },
      _type == "readinessTeamConfirmBlock" => {
        topBanner{ eyebrow, heading, subtext }, hashSlug,
        foundBadgeText, teamIdLabel, managerLabel, rankLabel, gwScoreLabel,
        confirmCtaText, searchAgainLinkText
      },
      _type == "readinessTemplateTeamBlock" => {
        topBanner{ eyebrow, heading, subtext }, hashSlug,
        templateSquads[]{ _key, title, description, optaPlayerIds },
        backLinkText, ctaText
      },
      _type == "readinessLeagueNameBlock" => {
        topBanner{ eyebrow, heading, subtext }, hashSlug,
        inputLabel, inputPlaceholder, maxLength, skipTitle, skipSubtext, ctaText
      },
      _type == "readinessCaptainBlock" => {
        topBanner{ eyebrow, heading, subtext }, hashSlug,
        pitchInstruction,
        matchFeedbackHeading, matchFeedbackBody,
        mismatchFeedbackHeading, mismatchFeedbackBody,
        skipLinkText, ctaText
      },
      _type == "readinessTransferBlock" => {
        topBanner{ eyebrow, heading, subtext }, hashSlug,
        pitchInstruction, replacementHeading,
        lockedRibbonText, lockedCardTitle, lockedCardHint,
        rollOptionText, ctaText
      },
      _type == "readinessStakesBlock" => {
        topBanner{ eyebrow, heading, subtext }, hashSlug,
        options[]{ _key, stakeKey, label, sublabel }, ctaText
      },
      _type == "readinessEmailGateBlock" => {
        topBanner{ eyebrow, heading, subtext }, hashSlug,
        obscuredScoreLabel, scoreReadyLabel,
        scoreCardLabel, scoreCardLockedLabel, loginLinkText, loginUrl,
        emailPlaceholder, consentText, invalidEmailText, ctaText,
        bannerSubtextBehind, bannerSubtextLevel, bannerSubtextAhead,
        trustpilotTemplateId, trustpilotHeight, trustpilotWidth,
        asSeenOnLabel, asSeenOnMonochrome, asSeenOnLogoHeight,
        asSeenOnItems[]{ _key, logo, altText, url }
      },
      _type == "readinessProcessingBlock" => {
        topBanner{ eyebrow, heading, subtext }, hashSlug,
        loadingSteps[]{ _key, label, durationMs }
      },
      _type == "readinessVerdictBlock" => {
        topBanner{ eyebrow, heading, subtext }, hashSlug,
        eyebrowTemplate, eyebrowFallback, gapThreshold,
        headlineHighGap, headlineLowGap, youLabel, winnersLabel, captionTemplate,
        pitchHeading, sellNoteDifferTemplate, sellNoteAgreeTemplate, sellNoteNoSellTemplate,
        oddsBands[]{ _key, minGap, maxGap, oddsPercentLabel, oddsLine, healLine },
        captainCalloutMatchTemplate, captainCalloutMismatchTemplate, captainCalloutSkippedTemplate,
        transferCalloutSelectedTemplate, transferCalloutRollTemplate, transferCalloutUndecidedTemplate,
        leagueCalloutTemplate, turnLine,
        promiseIcon, promiseHeadline, promiseTermsLine,
        ctaNamedLeagueTemplate, ctaLeagueTypeTemplate, ctaGenericText
      },
      _type == "readinessLeagueSelectBlock" => {
        topBanner{ eyebrow, heading, subtext }, hashSlug,
        rowStandingTemplate, rowStandingNoSizeTemplate,
        rowGapBehindTemplate, rowGapLevelTemplate, rowGapLeadingTemplate,
        loadingText, errorText, ctaText
      },
      _type == "readinessRaceVerdictBlock" => {
        topBanner{ eyebrow, heading, subtext }, hashSlug,
        celebrationEmoji, celebrationHeadline, celebrationBody,
        eyebrowTemplate, eyebrowFallback,
        headlineBehind, headlineLevel, headlineAhead, headlineLateStart,
        positionLabelTemplate, titlePaceLabel,
        youPillBehindTemplate, youPillLevelTemplate, youPillAheadTemplate, youPillLateStartLabel,
        ghostPillBehindTemplate, ghostPillLevelTemplate, ghostPillAheadLabel, ghostPillLateStartLabel,
        captionBehindTemplate, captionLevelTemplate, captionAheadTemplate, captionLateStartTemplate,
        trajectoryBehindTemplate, trajectoryLevelTemplate, trajectoryAheadTemplate, trajectoryLateStartTemplate,
        healBehind, healLevel, healAhead, healLateStart,
        pitchHeading, sellNoteDifferTemplate, sellNoteAgreeTemplate, sellNoteNoSellTemplate,
        captainCalloutMatchTemplate, captainCalloutMismatchTemplate, captainCalloutSkippedTemplate,
        captainCalloutBehindAppendTemplate,
        transferCalloutSelectedTemplate, transferCalloutRollTemplate, transferCalloutUndecidedTemplate,
        leagueCalloutBehindTemplate, leagueCalloutLevelTemplate, leagueCalloutAheadTemplate, leagueCalloutLateStartTemplate,
        turnLine, turnLineAhead,
        promiseIcon, promiseHeadline, promiseTermsLine,
        ctaTemplate, ctaAheadTemplate, ctaGenericText
      },
      _type == "paywallBlock" => {
        eyebrow, headline, subheadline, metricLabel, genericHeadline, genericSubheadline,
        tierCards[]{ _key, name, plan, benefitDescriptor, description, features, isRecommended, bestValueLabel },
        primaryCtaText, billedTodayTemplate, trialPeriodDays, trialBilledTodayTemplate, ctaPromiseIcon,
        checkoutCoupon,
        promiseEnabled, promiseIcon, promiseHeadline, promiseTermsLine,
        trustpilotTemplateId, trustpilotHeight, trustpilotWidth,
        asSeenOnLabel, asSeenOnItems[]{ _key, logo, altText, url },
        asSeenOnLogoHeight, asSeenOnMonochrome,
        testimonials[]{ _key, quote, authorName, winDetail, authorImage },
        shouldUseTrustpilotCarousel, trustpilotCarouselTemplateId, trustpilotCarouselHeight,
        miniLeagueWins[]{ _key, memberName, winClaim },
        hashSlug
      }
    }
  }`;

export const quizPageBySlugQuery = `
  *[_type == "quizPage" && slug.current == $slug][0] ${QUIZ_PAGE_PROJECTION}
`;

// Mirrors AI_TEAM_RATING_SETTINGS_ID in sanity/schemaTypes/aiTeamRatingSettings.ts.
// Kept as a local constant (as with MARKETING_COPY_SINGLETON_ID) because app code
// doesn't import from the Studio config directory.
export const AI_TEAM_RATING_SETTINGS_ID = 'aiTeamRatingSettings';

// Resolves the funnel served at /ai-team-rating: read the singleton settings
// doc and dereference the quizPage its activePage reference points at. The
// lookup is by document reference, not templateKey, because creator-branded
// variants legitimately share a templateKey with the canonical funnel and a
// key match cannot distinguish them. Falls back to the canonical gytr-pre-gw1
// funnel (matched by slug, which is unique per page) when the settings doc is
// absent or its reference is unset.
// The canonical funnel, used as the fallback for BOTH arms. See the alt query.
const CANONICAL_FUNNEL_SLUG = 'gytr-pre-gw1';

const buildAiTeamRatingQuery = (settingsId: string) => `
  coalesce(
    *[_id == "${settingsId}"][0]{
      "page": activePage-> ${QUIZ_PAGE_PROJECTION}
    }.page,
    *[_type == "quizPage" && slug.current == "${CANONICAL_FUNNEL_SLUG}"][0] ${QUIZ_PAGE_PROJECTION}
  )
`;

export const aiTeamRatingActivePageQuery = buildAiTeamRatingQuery(
  AI_TEAM_RATING_SETTINGS_ID
);

// Mirrors AI_TEAM_RATING_ALT_SETTINGS_ID in
// sanity/schemaTypes/aiTeamRatingSettingsAlt.ts.
export const AI_TEAM_RATING_ALT_SETTINGS_ID = 'aiTeamRatingSettingsAlt';

// FFH-1744. Resolves the alt-pricing funnel served at /ai-team-rating-v2.
//
// ⚠️ The fallback is the CONTROL page (gytr-pre-gw1), not the alt one. That is
// deliberate, not a copy-paste slip: if the alt settings doc is missing or its
// reference is unset, this arm degrades to normal pricing — the same direction
// as pulling the test — rather than silently selling the higher price from a
// half-configured state. Never point this fallback at the variant page.
export const aiTeamRatingAltActivePageQuery = buildAiTeamRatingQuery(
  AI_TEAM_RATING_ALT_SETTINGS_ID
);

// Mirrors AI_TEAM_RATING_V3_SETTINGS_ID in
// sanity/schemaTypes/aiTeamRatingSettingsV3.ts.
export const AI_TEAM_RATING_V3_SETTINGS_ID = 'aiTeamRatingSettingsV3';

// FFH-1743. Resolves the forever-discount funnel served at /ai-team-rating-v3.
//
// This arm sells the SAME prices as the control; only the paywall's
// `checkoutCoupon` differs (50% off every year instead of the first year only).
//
// ⚠️ The fallback is the CONTROL page (gytr-pre-gw1), for the same reason as the
// alt arm above: a missing or unset settings doc must degrade this URL to the
// standard offer — the same direction as pulling the test — rather than serving
// a forever discount from a half-configured state. Never point this fallback at
// the variant page.
export const aiTeamRatingV3ActivePageQuery = buildAiTeamRatingQuery(
  AI_TEAM_RATING_V3_SETTINGS_ID
);

export const getAllQuizPageSlugsQuery = () => `
  *[_type == "quizPage"] { "slug": slug.current }
`;

export const landingPageBySlugQuery = `
  *[_type == "landingPage" && slug.current == $slug][0] {
    _id, title, slug,
    hero {
      variant, eyebrow, heading, subheading, backgroundImage, ctaText,
      accentColour, creatorName, creatorHandle, creatorQuote, creatorPortrait,
      creatorVideo { label, youtubeUrl, posterImage, orientation }
    },
    productDemo {
      heading,
      items[]{ _key, featureName, benefit, description, screenshot }
    },
    countdown { isEnabled, headline, deadline, expiredMessage },
    paywall {
      "_type": "paywallBlock", "_key": "paywall",
      eyebrow, headline, subheadline, metricLabel, genericHeadline, genericSubheadline,
      tierCards[]{ _key, name, plan, benefitDescriptor, description, features, isRecommended, bestValueLabel },
      primaryCtaText, billedTodayTemplate, trialPeriodDays, trialBilledTodayTemplate, ctaPromiseIcon,
      checkoutCoupon,
      promiseEnabled, promiseIcon, promiseHeadline, promiseTermsLine,
      trustpilotTemplateId, trustpilotHeight, trustpilotWidth,
      asSeenOnLabel, asSeenOnItems[]{ _key, logo, altText, url },
      asSeenOnLogoHeight, asSeenOnMonochrome,
      testimonials[]{ _key, quote, authorName, winDetail, authorImage },
      shouldUseTrustpilotCarousel, trustpilotCarouselTemplateId, trustpilotCarouselHeight,
      miniLeagueWins[]{ _key, memberName, winClaim }
    },
    faq { heading, items[]{ _key, question, answer } },
    seo { metaTitle, metaDescription, ogImage, canonicalUrl, isHiddenFromSearch }
  }
`;

export const getAllLandingPageSlugsQuery = () => `
  *[_type == "landingPage" && defined(slug.current)] { "slug": slug.current }
`;

/**
 * Shared by articles and team reveals. Never add `premiumBody` here: this
 * projection feeds getStaticProps, and a static payload is readable by anyone.
 */
const ARTICLE_DOCUMENT_PROJECTION = `
    _type,
    title,
    slug,
    standfirst,
    publishedAt,
    lastUpdatedAt,
    _createdAt,
    _updatedAt,
    showTableOfContents,
    showRail,
    gameweek,
    category->{ title, "slug": slug.current },
    author->{
      name,
      "slug": slug.current,
      bio,
      teamRevealBio,
      twitterHandle,
      affiliateCode,
      avatar { ..., asset->{ _id, url } }
    },
    featuredImage {
      ...,
      asset->{
        _id,
        url,
        metadata { dimensions }
      }
    },
    body[]{
      ...,
      _type == "image" => {
        ...,
        asset->{
          _id,
          url,
          metadata { dimensions }
        }
      }
    },
    accessLevel,
    seo {
      metaTitle,
      metaDescription,
      ogImage { ..., asset->{ _id, url, metadata { dimensions } } },
      canonicalUrl,
      isHiddenFromSearch
    }
`;

export const articlePageBySlugQuery = `
  *[_type == "articlePage" && slug.current == $slug][0] {${ARTICLE_DOCUMENT_PROJECTION}
  }
`;

/**
 * Preview only. The draft readers run in getServerSideProps behind the preview
 * token with caching disabled, so this is the one place the premium body may be
 * projected: editors need to see both halves before publishing.
 */
const ARTICLE_DRAFT_PROJECTION = `${ARTICLE_DOCUMENT_PROJECTION},
    premiumBody[]{
      ...,
      _type == "image" => {
        ...,
        asset->{
          _id,
          url,
          metadata { dimensions }
        }
      }
    }
`;

export const articlePageDraftBySlugQuery = `
  *[_type == "articlePage" && slug.current == $slug][0] {${ARTICLE_DRAFT_PROJECTION}
  }
`;

/**
 * A reveal is addressed by its author as well as its slug, because it renders
 * under the contributor's page and two contributors may reuse a title.
 */
export const teamRevealBySlugQuery = `
  *[_type == "teamReveal" && slug.current == $slug && author->slug.current == $authorSlug][0] {${ARTICLE_DOCUMENT_PROJECTION}
  }
`;

export const teamRevealDraftBySlugQuery = `
  *[_type == "teamReveal" && slug.current == $slug && author->slug.current == $authorSlug][0] {${ARTICLE_DRAFT_PROJECTION}
  }
`;

/**
 * The predicted lineups page (FFH-2917). Never add `premiumTeams` here: this
 * projection feeds getStaticProps, and a static payload is readable by anyone.
 * The eleven for each club is not in Sanity at all; the page reads it from the
 * API for the document's gameweek.
 */
const PREDICTED_LINEUPS_AVAILABILITY_PROJECTION = `{
      _key,
      detail,
      player { optaUuid, displayName, teamShortName }
    }`;

const PREDICTED_LINEUPS_TEAM_SECTION_PROJECTION = `{
      _key,
      teamShortName,
      teamName,
      opponentShortName,
      opponentName,
      isHome,
      gameweek,
      kickoffAt,
      updatedAt,
      notes[]{
        ...,
        _type == "image" => {
          ...,
          asset->{
            _id,
            url,
            metadata { dimensions }
          }
        }
      },
      ruledOut[]${PREDICTED_LINEUPS_AVAILABILITY_PROJECTION},
      doubts[]${PREDICTED_LINEUPS_AVAILABILITY_PROJECTION},
      suspensions[]${PREDICTED_LINEUPS_AVAILABILITY_PROJECTION}
    }`;

const PREDICTED_LINEUPS_DOCUMENT_PROJECTION = `
    _type,
    _updatedAt,
    gameweek,
    title,
    standfirst,
    intro[]{
      ...,
      _type == "image" => {
        ...,
        asset->{
          _id,
          url,
          metadata { dimensions }
        }
      }
    },
    pressConferences[]{ _key, teamShortName, manager, time, note },
    keyHeadlines,
    teams[]${PREDICTED_LINEUPS_TEAM_SECTION_PROJECTION},
    accessLevel,
    seo {
      metaTitle,
      metaDescription,
      ogImage { ..., asset->{ _id, url, metadata { dimensions } } },
      canonicalUrl,
      isHiddenFromSearch
    }
`;

export const predictedLineupsPageQuery = `
  *[_type == "predictedLineupsPage" && _id == $id][0] {${PREDICTED_LINEUPS_DOCUMENT_PROJECTION}
  }
`;

/**
 * Preview only, behind the preview token with caching disabled, so the gated
 * club sections may be projected for the editor.
 */
export const predictedLineupsPageDraftQuery = `
  *[_type == "predictedLineupsPage" && _id == $id][0] {${PREDICTED_LINEUPS_DOCUMENT_PROJECTION},
    premiumTeams[]${PREDICTED_LINEUPS_TEAM_SECTION_PROJECTION}
  }
`;
